// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Stairs (frontend-u0o.10, slice S8; design.md §4, mock 06 — Revit's stair
 * by run): the rows `GET /stairs` serves (ReUseX `core::StairRecord`,
 * ruxd #35) on the plan and in Egenskaber, and what the Trappe tool sends.
 *
 *  - The rise: a stair climbs from its storey's floor to the floor of the
 *    storey above (or to a chosen one; on the top storey to its ceiling,
 *    arriving nowhere). Enough risers to stay at or under 18 cm; the 2s + g
 *    rule of thumb (Danish building regulations' guidance, BR18) wants
 *    2 x riser + going between 59 and 65 cm.
 *  - The layout: the drawn line is run 1's walking line from its first
 *    riser; a run of k risers is k - 1 goings long. An L or U stair turns
 *    on a landing one width deep (L: square; U: across both runs) and the
 *    rest of the risers go in run 2. `location` puts the walking line on
 *    the left edge, the middle or the right edge of the width, seen walking
 *    up — the solids stage's `run_frame` (ReUseX `reconstruction/solids.cpp`).
 *  - The plan symbol (Danish drawing practice): a line per riser, the
 *    walking line from a dot at the bottom to an arrow at the top, a break
 *    line where the plan cuts the stair (1.2 m up) and the risers above it
 *    dashed.
 *
 * World x/y. Pure: no React, no DOM.
 */

import type { ModelEditOp, PlanXY, Stair, StairRun, Storey } from '../api/types';
import type { GeometriModel } from './geometri';
import type { EditField, PropertiesView, PropertySection } from './geometriEdit';
import type { XY } from './planWalls';

export type StairMode = Stair['kind'];
export type StairLocation = Stair['location'];

/** The Trappe tool's options strip. */
export interface StairOptions {
  /** Lige løb | L-løb | U-løb (Spindel is not drawn yet). */
  mode: StairMode;
  /** Metres. */
  width: number;
  location: StairLocation;
  /** The going a drawn run is laid out in, metres. */
  going: number;
  /** The storey it arrives at: undefined = the one above; null = none (a few steps within the storey). */
  toStorey: number | null | undefined;
  /** Cut the stair's footprint out of the floor it arrives at. */
  cutSlab: boolean;
}

export const DEFAULT_STAIR_OPTIONS: StairOptions = { mode: 'straight', width: 1, location: 'centre', going: 0.27, toStorey: undefined, cutSlab: true };

/** BR18's guidance (and ReUseX `stair_advice`). */
export const MAX_RISER = 0.18;
export const MAX_RISER_DWELLING = 0.2;
export const COMFORT_MIN = 0.59;
export const COMFORT_MAX = 0.65;
export const MIN_GOING = 0.25;
export const MIN_WIDTH = 0.8;
/** The riser the 2s + g suggestion aims for at most, and no lower than this. */
const MIN_SENSIBLE_RISER = 0.12;
/** Where the plan cuts a stair, above its floor. */
export const PLAN_CUT = 1.2;

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6 + 0;
const pt = (p: XY): PlanXY => [r6(p[0]), r6(p[1])];
/** The right normal of a direction (clockwise), as the solids stage's run frame. */
const rightOf = (d: XY): XY => [d[1], -d[0]];
const leftOf = (d: XY): XY => [-d[1], d[0]];

function unit(v: XY): XY | null {
  const L = Math.hypot(v[0], v[1]);
  return L > 1e-9 ? [v[0] / L, v[1] / L] : null;
}

/** "19,3 cm". */
export const cmText = (m: number) => `${(m * 100).toFixed(1).replace('.', ',')} cm`;
/** "0,80 m". */
const mText = (m: number) => `${m.toFixed(2).replace('.', ',')} m`;

// ------------------------------------------------------------ labels --

const KIND_LABEL: Record<StairMode, string> = { straight: 'Lige løb', L: 'L-løb', U: 'U-løb', spiral: 'Spindeltrappe' };

export function stairKindLabel(kind: StairMode): string {
  return KIND_LABEL[kind] ?? 'Trappe';
}

export function stairLabel(s: Pick<Stair, 'id'>): string {
  return `Trappe ${s.id}`;
}

// --------------------------------------------------------------- rise --

export interface StairRise {
  /** World z of the floor it starts on. */
  zBase: number;
  /** World z it climbs to. */
  zTop: number;
  /** The storey it arrives at; null = none. */
  toId: number | null;
}

/**
 * How far a stair on storey `storeyId` climbs: to the floor of `toId`, by
 * default the next storey up; with none above (or `toId` null) to the
 * storey's own ceiling. Null for an unknown storey.
 */
export function stairRise(storeys: readonly Storey[] | null | undefined, storeyId: number | null, toId?: number | null): StairRise | null {
  const from = storeys?.find((s) => s.id === storeyId);
  if (!from) return null;
  if (toId === undefined) {
    const above = [...(storeys ?? [])].filter((s) => s.z_min > from.z_min + 0.05).sort((a, b) => a.z_min - b.z_min)[0];
    return above ? { zBase: from.z_min, zTop: above.z_min, toId: above.id } : { zBase: from.z_min, zTop: from.z_max, toId: null };
  }
  const to = toId === null ? null : storeys?.find((s) => s.id === toId);
  return to ? { zBase: from.z_min, zTop: to.z_min, toId: to.id } : { zBase: from.z_min, zTop: from.z_max, toId: null };
}

/** The fewest risers that keep each at or under 18 cm. */
export function defaultRisers(rise: number): number {
  return Math.max(1, Math.ceil(rise / MAX_RISER - 1e-6));
}

/** 2 x riser + going, metres. */
export function comfort(riser: number, going: number): number {
  return 2 * riser + going;
}

export const comfortOk = (v: number) => v >= COMFORT_MIN - 1e-9 && v <= COMFORT_MAX + 1e-9;

/**
 * The riser count that puts 2s + g in the band nearest 62 cm with every
 * riser at or under 18 cm (and no lower than 12 cm); null when none does.
 */
export function suggestRisers(rise: number, going: number): { risers: number; value: number } | null {
  if (!(rise > 0)) return null;
  let best: { risers: number; value: number } | null = null;
  const lo = Math.max(1, Math.ceil(rise / MAX_RISER - 1e-6));
  const hi = Math.floor(rise / MIN_SENSIBLE_RISER + 1e-6);
  for (let n = lo; n <= hi; n += 1) {
    const value = comfort(rise / n, going);
    if (!comfortOk(value)) continue;
    if (!best || Math.abs(value - 0.62) < Math.abs(best.value - 0.62)) best = { risers: n, value };
  }
  return best;
}

/**
 * What a stair breaks of the guidance, in Danish (ReUseX `stair_advice`'s
 * rules): a riser over 20 cm (over 18 cm: only inside a dwelling), 2s + g
 * outside 59–65 cm (with the riser count that fixes it, when one does), a
 * going under 25 cm, a width under 0,80 m. Each once.
 */
export function stairAdvice(s: { riser: number | null; goings: readonly (number | null)[]; widths: readonly number[]; rise?: number }): string[] {
  const out: string[] = [];
  const push = (t: string) => {
    if (!out.includes(t)) out.push(t);
  };
  const h = s.riser;
  if (h === null || !(h > 0)) return out;
  if (h > MAX_RISER_DWELLING + 1e-9) push(`Stigning ${cmText(h)} er over 20 cm — tilføj trin`);
  else if (h > MAX_RISER + 1e-9) push(`Stigning ${cmText(h)} er over 18 cm — kun i en bolig`);
  const rise = s.rise ?? null;
  for (const g of s.goings) {
    if (g === null) continue;
    const v = comfort(h, g);
    if (!comfortOk(v)) {
      const fix = rise === null ? null : suggestRisers(rise, g);
      push(`2s + g = ${cmText(v)}, uden for 59–65 cm${fix ? ` · ${fix.risers} trin giver ${cmText(fix.value)}` : ''}`);
    }
  }
  for (const g of s.goings) if (g !== null && g < MIN_GOING - 1e-9) push(`Grund ${cmText(g)} er under 25 cm`);
  for (const w of s.widths) if (w < MIN_WIDTH - 1e-9) push(`Bredde ${mText(w)} er under 0,80 m`);
  return out;
}

// --------------------------------------------------------------- runs --

/** A run's frame: start, direction, right normal, length and the across range its width covers. */
export interface RunFrame {
  p0: XY;
  d: XY;
  right: XY;
  len: number;
  /** Right-normal coordinate of the band's left edge (the band is lo .. lo + width). */
  lo: number;
  width: number;
}

/** Where the band starts across the walking line (right-normal coordinate): ReUseX `run_frame`. */
const lowOf = (location: StairLocation, width: number) => (location === 'left' ? 0 : location === 'right' ? -width : -width / 2);

export function runFrame(run: Pick<StairRun, 'start' | 'end' | 'width'>, location: StairLocation): RunFrame {
  const p0: XY = [run.start[0], run.start[1]];
  const v = sub([run.end[0], run.end[1]], p0);
  const len = Math.hypot(v[0], v[1]);
  const d: XY = len > 1e-9 ? [v[0] / len, v[1] / len] : [1, 0];
  return { p0, d, right: rightOf(d), len, lo: lowOf(location, run.width), width: run.width };
}

/** A run's footprint, counter-clockwise (the solids stage's `run_rect`). */
export function runRect(run: Pick<StairRun, 'start' | 'end' | 'width'>, location: StairLocation): XY[] {
  const f = runFrame(run, location);
  const at = (s: number, w: number): XY => [r6(f.p0[0] + s * f.d[0] + w * f.right[0]), r6(f.p0[1] + s * f.d[1] + w * f.right[1])];
  return [at(0, f.lo + f.width), at(f.len, f.lo + f.width), at(f.len, f.lo), at(0, f.lo)];
}

// ------------------------------------------------------------- layout --

export interface LayoutInput {
  /** Run 1's walking line starts here (its first riser). */
  start: XY;
  /** Run 1's direction (unit). */
  dir: XY;
  /** Risers in run 1 (the rest go in run 2). */
  first: number;
  total: number;
  width: number;
  location: StairLocation;
  /** Metres. */
  going: number;
  mode: 'straight' | 'L' | 'U';
  /** Run 2 turns left (1) or right (−1) of run 1. */
  turn: 1 | -1;
}

export interface StairLayout {
  runs: StairRun[];
  landings: { outer: PlanXY[]; holes: PlanXY[][] }[];
}

function ccwRing(ring: XY[]): PlanXY[] {
  let a = 0;
  for (let i = 0; i < ring.length; i += 1) a += crs(ring[i], ring[(i + 1) % ring.length]);
  return (a < 0 ? [...ring].reverse() : ring).map(pt);
}

/**
 * The runs and landing of a stair drawn by run: run 1 from `start` along
 * `dir`, `first` risers (k − 1 goings); with risers left, a landing one
 * width deep and run 2 — straight on, turned a quarter (L) or turned back
 * beside run 1 (U), to the `turn` side.
 */
export function layoutStair(i: LayoutInput): StairLayout {
  const total = Math.max(1, Math.round(i.total));
  const mode = i.mode === 'straight' || i.mode === 'L' || i.mode === 'U' ? i.mode : 'straight';
  const k1 = Math.min(Math.max(1, Math.round(i.first)), total);
  const k2 = total - k1;
  const d = unit(i.dir) ?? [1, 0];
  const w = i.width;
  const g = i.going;
  const off = lowOf(i.location, w) + w / 2;
  /** The centre line's point for a walking line point (and back). */
  const toCentre = (p: XY, dir: XY): XY => add(p, mul(rightOf(dir), off));
  const toWalk = (c: XY, dir: XY): XY => sub(c, mul(rightOf(dir), off));
  const end1 = add(i.start, mul(d, (k1 - 1) * g));
  const runs: StairRun[] = [{ start: pt(i.start), end: pt(end1), width: r6(w), risers: k1 }];
  if (k2 === 0) return { runs, landings: [] };
  const c1 = toCentre(end1, d);
  const across = mul(rightOf(d), w / 2);
  let dir2: XY;
  let c2: XY;
  let ring: XY[];
  if (mode === 'straight') {
    dir2 = d;
    c2 = add(c1, mul(d, w));
    ring = [sub(c1, across), add(c1, across), add(add(c1, mul(d, w)), across), sub(add(c1, mul(d, w)), across)];
  } else {
    const n2 = mul(leftOf(d), i.turn);
    if (mode === 'L') {
      dir2 = n2;
      c2 = add(add(c1, mul(d, w / 2)), mul(n2, w / 2));
      ring = [sub(c1, across), add(c1, across), add(add(c1, mul(d, w)), across), sub(add(c1, mul(d, w)), across)];
    } else {
      dir2 = mul(d, -1);
      c2 = add(c1, mul(n2, w));
      const near = mul(n2, -w / 2);
      const far = mul(n2, w * 1.5);
      ring = [add(c1, near), add(c1, far), add(add(c1, mul(d, w)), far), add(add(c1, mul(d, w)), near)];
    }
  }
  const start2 = toWalk(c2, dir2);
  const end2 = add(start2, mul(dir2, (k2 - 1) * g));
  runs.push({ start: pt(start2), end: pt(end2), width: r6(w), risers: k2 });
  return { runs, landings: [{ outer: ccwRing(ring), holes: [] }] };
}

/** A run's going: the stair's tread depth, else its length over its goings (null for one riser). */
export function runGoing(s: Pick<Stair, 'tread_depth'>, run: StairRun): number | null {
  if (s.tread_depth !== null && s.tread_depth > 0) return s.tread_depth;
  return run.risers > 1 ? dist(run.start, run.end) / (run.risers - 1) : null;
}

/**
 * How a stair of one or two runs was laid out (start, direction, risers,
 * going, turn) — to lay it out again with one value changed. Null for
 * anything else (three runs, a spiral, no direction).
 */
export function layoutOf(s: Pick<Stair, 'runs' | 'location' | 'tread_depth' | 'kind'> & Partial<Pick<Stair, 'landings'>>): LayoutInput | null {
  const runs = s.runs;
  if (runs.length === 0 || runs.length > 2 || s.kind === 'spiral') return null;
  const r1 = runs[0];
  const d1 = unit(sub(r1.end, r1.start));
  const goings = runs.map((r) => runGoing(s, r)).filter((g): g is number => g !== null && g > 0);
  const going = goings[0] ?? 0.27;
  const base = { start: [r1.start[0], r1.start[1]] as XY, width: r1.width, location: s.location, going, first: r1.risers };
  if (runs.length === 1) return { ...base, dir: d1 ?? [1, 0], total: r1.risers, mode: 'straight', turn: 1 };
  const r2 = runs[1];
  const d2 = unit(sub(r2.end, r2.start));
  const dir = d1 ?? d2;
  if (!dir) return null;
  const total = r1.risers + r2.risers;
  const second = d2 ?? dir;
  const c = dot(dir, second);
  if (c > 0.9) return { ...base, dir, total, mode: 'straight', turn: 1 };
  if (c < -0.9) {
    const off = lowOf(s.location, r1.width) + r1.width / 2;
    const c1 = add(r1.end, mul(rightOf(dir), off));
    const c2 = add(r2.start, mul(rightOf(second), off));
    return { ...base, dir, total, mode: 'U', turn: crs(dir, sub(c2, c1)) >= 0 ? 1 : -1 };
  }
  return { ...base, dir, total, mode: 'L', turn: crs(dir, second) >= 0 ? 1 : -1 };
}

// ------------------------------------------------------------ derived --

/** The rows' derived fields as ruxd serves them: riser count and height, goings, advice (Danish here). */
export function deriveStair(s: Stair): Stair {
  const riser_count = s.runs.reduce((n, r) => n + r.risers, 0);
  const rise = s.z_top - s.z_base;
  const riser_height = riser_count > 0 ? rise / riser_count : null;
  const goings = s.runs.map((r) => runGoing(s, r));
  return { ...s, riser_count, riser_height, goings, advice: stairAdvice({ riser: riser_height, goings, widths: s.runs.map((r) => r.width), rise }) };
}

/** The stairs on storey `storeyId` (all of them when null). */
export function stairsOn(model: Pick<GeometriModel, 'stairs'>, storeyId: number | null): Stair[] {
  return (model.stairs ?? []).filter((s) => storeyId === null || s.storey_id === storeyId);
}

/** Every ring a stair covers: its runs (of two risers or more) and its landings. */
export function stairFootprint(s: Pick<Stair, 'runs' | 'landings' | 'location'>): XY[][] {
  const out: XY[][] = [];
  for (const r of s.runs) if (r.risers > 1) out.push(runRect(r, s.location));
  for (const l of s.landings) if (l.outer.length >= 3) out.push(l.outer.map((p) => [p[0], p[1]] as XY));
  return out;
}

function inRing(ring: readonly XY[], p: XY): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The stair whose footprint holds `p` (the smallest when several do). */
export function stairAt(stairs: readonly Stair[], p: XY): Stair | null {
  for (const s of stairs) if (stairFootprint(s).some((r) => inRing(r, p))) return s;
  return null;
}

// --------------------------------------------------------- the symbol --

export interface RunSymbol {
  /** The footprint, counter-clockwise. */
  rect: XY[];
  /** One line across the width per riser, bottom first. */
  risers: [XY, XY][];
  /** Per riser line: its top is above the plan cut (drawn dashed, beyond). */
  above: boolean[];
}

export interface StairSymbol {
  runs: RunSymbol[];
  landings: XY[][];
  /** The walking line, bottom to top, round the landings. */
  walk: XY[];
  /** The bottom: a dot. */
  start: XY;
  /** The top: an arrow, at the last riser, pointing the way up. */
  arrow: { tip: XY; dir: XY };
  /** The break line across the run where the plan cuts the stair; null when it never reaches the cut. */
  cut: [XY, XY] | null;
}

/** The plan symbol of a stair (see the module comment); `cutAt` metres above its floor. */
export function stairSymbol(s: Pick<Stair, 'runs' | 'landings' | 'location' | 'z_base' | 'z_top' | 'tread_depth'>, cutAt = PLAN_CUT): StairSymbol {
  const n = s.runs.reduce((k, r) => k + r.risers, 0);
  const h = n > 0 ? (s.z_top - s.z_base) / n : 0;
  const runs: RunSymbol[] = [];
  let below = 0;
  let cut: [XY, XY] | null = null;
  const walk: XY[] = [];
  s.runs.forEach((r, ri) => {
    const f = runFrame(r, s.location);
    const g = r.risers > 1 ? f.len / (r.risers - 1) : 0;
    const across = (at: number): [XY, XY] => [add(add(f.p0, mul(f.d, at)), mul(f.right, f.lo)), add(add(f.p0, mul(f.d, at)), mul(f.right, f.lo + f.width))];
    const risers: [XY, XY][] = [];
    const above: boolean[] = [];
    for (let j = 0; j < r.risers; j += 1) {
      const top = (below + j + 1) * h;
      const isAbove = top > cutAt + 1e-9;
      if (isAbove && cut === null && j > 0 && r.risers > 1) {
        // Diagonal across the tread below the first riser above the cut.
        const a = add(add(f.p0, mul(f.d, (j - 0.85) * g)), mul(f.right, f.lo));
        const b = add(add(f.p0, mul(f.d, (j - 0.15) * g)), mul(f.right, f.lo + f.width));
        cut = [a, b];
      }
      risers.push(across(j * g));
      above.push(isAbove);
    }
    runs.push({ rect: runRect(r, s.location), risers, above });
    // The walking line: this run, joined to the previous one round the landing.
    const a: XY = [r.start[0], r.start[1]];
    const b: XY = [r.end[0], r.end[1]];
    if (ri > 0) {
      const prev = s.runs[ri - 1];
      const pd = unit(sub(prev.end, prev.start));
      if (pd && f.len > 1e-9) {
        const c = dot(pd, f.d);
        if (Math.abs(c) < 0.1) {
          const den = crs(pd, f.d);
          const t = crs(sub(a, prev.end), f.d) / den;
          walk.push(add(prev.end, mul(pd, t)));
        } else if (c < -0.9) {
          const depth = s.landings[ri - 1] ? r.width / 2 : 0;
          walk.push(add(prev.end, mul(pd, depth)), add(a, mul(pd, depth)));
        }
      }
    }
    walk.push(a, b);
    below += r.risers;
  });
  const last = s.runs[s.runs.length - 1];
  const lastDir = last ? (unit(sub(last.end, last.start)) ?? runFrame(last, s.location).d) : ([1, 0] as XY);
  const first = s.runs[0];
  return {
    runs,
    landings: s.landings.map((l) => l.outer.map((p) => [p[0], p[1]] as XY)),
    walk,
    start: first ? [first.start[0], first.start[1]] : [0, 0],
    arrow: { tip: last ? [last.end[0], last.end[1]] : [0, 0], dir: lastDir },
    cut,
  };
}

// --------------------------------------------------------------- ops --

export interface CreateStairInput {
  storeyId: number;
  rise: StairRise;
  layout: StairLayout;
  mode: StairMode;
  location: StairLocation;
  cutSlab: boolean;
  guid: string;
}

/** A drawn stair as one `stair.create`; the reason in Danish when it cannot be one. */
export function createStairOps(i: CreateStairInput): { ops: ModelEditOp[]; label: string } | { error: string } {
  if (!(i.rise.zTop > i.rise.zBase + 1e-6)) return { error: 'Trappen skal stige: etagen har ingen højde' };
  if (i.layout.runs.length === 0) return { error: 'Tegn trappens første løb' };
  return {
    label: 'Ny trappe',
    ops: [
      {
        op: 'stair.create',
        record: {
          guid: i.guid,
          storey_id: i.storeyId,
          storey_to_id: i.rise.toId,
          kind: i.layout.runs.length > 1 ? i.mode : 'straight',
          location: i.location,
          runs: i.layout.runs,
          landings: i.layout.landings,
          z_base: r6(i.rise.zBase),
          z_top: r6(i.rise.zTop),
          tread_depth: null,
          cut_slab_above: i.cutSlab && i.rise.toId !== null,
        },
      },
    ],
  };
}

/** Vend: the stair walked the other way — the runs in reverse, each from its end; the walking line keeps its side. */
export function flipStairOps(s: Stair): ModelEditOp[] {
  const runs = [...s.runs].reverse().map((r) => ({ ...r, start: r.end, end: r.start }));
  const location = s.location === 'left' ? 'right' : s.location === 'right' ? 'left' : 'centre';
  return [{ op: 'stair.update', guid: s.guid, set: { runs, location } }];
}

/** The stair laid out again from `layout` (runs and landings), as an update. */
function relayout(s: Stair, layout: LayoutInput): ModelEditOp[] {
  const lay = layoutStair(layout);
  return [{ op: 'stair.update', guid: s.guid, set: { runs: lay.runs, landings: lay.landings, location: layout.location, tread_depth: null } }];
}

export type StairFieldKey = 'stair_going' | 'stair_risers' | 'stair_width' | 'stair_location' | 'stair_to' | 'stair_cut';

const same = (a: number, b: number) => Math.abs(a - b) < 5e-4;

/**
 * The ops that set one of a stair's fields: Grund, Trin, Bredde and
 * Placering lay a stair drawn by run out again (from its first riser);
 * Til etage moves its top to that storey's floor (or its own ceiling);
 * Skær hul toggles the slab cut. Null when invalid or unchanged.
 */
export function stairFieldOps(model: GeometriModel, s: Stair, key: StairFieldKey | string, value: number | string): ModelEditOp[] | null {
  const lay = layoutOf(s);
  const n = typeof value === 'number' ? value : Number.NaN;
  switch (key) {
    case 'stair_going': {
      if (!(n >= 0.1 && n <= 0.6) || !lay || same(n, lay.going)) return null;
      return relayout(s, { ...lay, going: n });
    }
    case 'stair_risers': {
      const t = String(value).trim();
      if (!/^\d+$/.test(t)) return null;
      const total = Number(t);
      if (total < 1 || total > 60 || !lay || total === lay.total) return null;
      const first = lay.mode === 'straight' && s.runs.length === 1 ? total : Math.min(lay.first, Math.max(1, total - 1));
      return relayout(s, { ...lay, total, first });
    }
    case 'stair_width': {
      if (!(n >= 0.3 && n <= 5)) return null;
      if (s.runs.every((r) => same(r.width, n))) return null;
      if (lay) return relayout(s, { ...lay, width: n });
      return [{ op: 'stair.update', guid: s.guid, set: { runs: s.runs.map((r) => ({ ...r, width: r6(n) })) } }];
    }
    case 'stair_location': {
      if ((value !== 'left' && value !== 'centre' && value !== 'right') || value === s.location) return null;
      if (lay) return relayout(s, { ...lay, location: value });
      return [{ op: 'stair.update', guid: s.guid, set: { location: value } }];
    }
    case 'stair_to': {
      const to = value === 'none' ? null : Number(value);
      if (to !== null && !Number.isInteger(to)) return null;
      if (to === s.storey_to_id) return null;
      const rise = stairRise(model.storeys, s.storey_id, to);
      if (!rise || !(rise.zTop > rise.zBase)) return null;
      return [{ op: 'stair.update', guid: s.guid, set: { storey_to_id: rise.toId, z_top: r6(rise.zTop), ...(rise.toId === null ? { cut_slab_above: false } : {}) } }];
    }
    case 'stair_cut': {
      const on = value === 'yes';
      if (on === s.cut_slab_above || (on && s.storey_to_id === null)) return null;
      return [{ op: 'stair.update', guid: s.guid, set: { cut_slab_above: on } }];
    }
    default:
      return null;
  }
}

// --------------------------------------------------- properties panel --

const LOCATIONS = [
  { value: 'left', label: 'Venstre' },
  { value: 'centre', label: 'Midte' },
  { value: 'right', label: 'Højre' },
];

const ro = (key: EditField['key'], label: string, display: string): EditField => ({ key, label, type: 'text', value: null, display, edited: false, detected: null, editable: false });

/** Egenskaber for a stair: Beregnet fra etagehøjde (riser, going, risers, run length, 2s + g), Mål, Hul i dæk. */
export function stairProperties(model: GeometriModel, s: Stair): PropertiesView {
  const d = deriveStair(s);
  const storeyName = (id: number | null) => (id === null ? 'Ingen' : (model.storeys?.find((x) => x.id === id)?.name ?? `Etage ${id}`));
  const going = d.goings.find((g): g is number => g !== null) ?? null;
  const v = d.riser_height !== null && going !== null ? comfort(d.riser_height, going) : null;
  const runLength = s.runs.reduce((a, r) => a + dist(r.start, r.end), 0);
  const lay = layoutOf(s);
  const width = s.runs[0]?.width ?? 0;
  const computed: PropertySection = {
    title: 'Beregnet fra etagehøjde',
    fields: [
      ro('stair_riser', 'Stigning', d.riser_height !== null ? cmText(d.riser_height) : 'ukendt'),
      {
        key: 'stair_going',
        label: 'Grund',
        type: 'length',
        value: going,
        display: going !== null ? cmText(going) : 'ukendt',
        edited: false,
        detected: null,
        editable: lay !== null,
      },
      { key: 'stair_risers', label: 'Trin', type: 'text', value: String(d.riser_count), display: String(d.riser_count), edited: false, detected: null, editable: lay !== null },
      ro('stair_run', 'Løbslængde', mText(runLength)),
      ro('stair_comfort', '2s + g', v !== null ? cmText(v) : 'ukendt'),
    ],
  };
  const size: PropertySection = {
    title: 'Mål',
    fields: [
      { key: 'stair_width', label: 'Bredde', type: 'length', value: width, display: mText(width), edited: false, detected: null, editable: true },
      { key: 'stair_location', label: 'Ganglinje', type: 'choice', value: s.location, display: LOCATIONS.find((l) => l.value === s.location)?.label ?? s.location, options: LOCATIONS, edited: false, detected: null, editable: true },
      ro('stair_from', 'Fra', storeyName(s.storey_id)),
      {
        key: 'stair_to',
        label: 'Til',
        type: 'choice',
        value: s.storey_to_id === null ? 'none' : String(s.storey_to_id),
        display: storeyName(s.storey_to_id),
        // The storey above (and the one it arrives at now, if another): a stair seldom skips a floor.
        options: [
          ...[...(model.storeys ?? [])]
            .filter((x) => x.id !== s.storey_id && x.z_min > s.z_base + 0.05)
            .sort((a, b) => a.z_min - b.z_min)
            .filter((x, i) => i === 0 || x.id === s.storey_to_id)
            .map((x) => ({ value: String(x.id), label: x.name })),
          { value: 'none', label: 'Ingen' },
        ],
        edited: false,
        detected: null,
        editable: true,
      },
    ],
  };
  const hole: PropertySection = {
    title: 'Hul i dæk',
    fields: [
      s.storey_to_id === null
        ? ro('stair_cut', 'Skær hul', 'Når ingen etage')
        : {
            key: 'stair_cut',
            label: `Skær hul i ${storeyName(s.storey_to_id)}`,
            type: 'choice',
            value: s.cut_slab_above ? 'yes' : 'no',
            display: s.cut_slab_above ? 'Ja' : 'Nej',
            options: [
              { value: 'yes', label: 'Ja' },
              { value: 'no', label: 'Nej' },
            ],
            edited: false,
            detected: null,
            editable: true,
          },
    ],
  };
  const chips: PropertiesView['chips'] = [{ label: s.edited ? 'Redigeret' : s.source === 'manual' ? 'Manuel' : s.source === 'imported' ? 'Importeret' : 'Detekteret', tone: s.edited ? 'accent' : 'neutral' }];
  if (d.advice.length > 0) chips.push({ label: d.advice.length === 1 ? '1 råd' : `${d.advice.length} råd`, tone: 'warning' });
  else chips.push({ label: 'BR18 ok', tone: 'success' });
  return {
    guid: s.guid,
    kind: 'stair',
    eyebrow: `Trappe · ${stairKindLabel(s.kind)}`,
    title: stairLabel(s),
    chips,
    sections: [computed, size, hole],
    revertable: s.edited || s.source === 'manual',
    deletable: true,
    related: [],
  };
}

// ------------------------------------------------------------ profile --

/**
 * The stair seen from the side, unfolded along its walking line: (along,
 * height) from the bottom riser, a riser up and a going on per step, a
 * landing one long tread, the last riser up onto the floor above.
 * Relative to `z_base`.
 */
export function stairProfile(s: Pick<Stair, 'runs' | 'landings' | 'z_base' | 'z_top' | 'tread_depth'>): { points: [number, number][]; length: number; rise: number } {
  const n = s.runs.reduce((k, r) => k + r.risers, 0);
  const rise = s.z_top - s.z_base;
  const h = n > 0 ? rise / n : 0;
  const pts: [number, number][] = [[0, 0]];
  let x = 0;
  let zz = 0;
  s.runs.forEach((r, i) => {
    const g = runGoing(s, r) ?? 0;
    for (let j = 0; j < r.risers; j += 1) {
      zz += h;
      pts.push([r6(x), r6(zz)]);
      const last = j === r.risers - 1;
      // After a run's last riser: the landing (one width), or the floor above (a going's worth, drawn).
      const tread = last ? (i < s.runs.length - 1 ? (s.landings[i] ? r.width : g) : g) : g;
      x += tread;
      pts.push([r6(x), r6(zz)]);
    }
  });
  const length = r6(x - (s.runs.length > 0 ? (runGoing(s, s.runs[s.runs.length - 1]) ?? 0) : 0));
  return { points: pts, length, rise };
}

// -------------------------------------------------------------- grips --

export interface StairGrip {
  /** `end`: the top of the last run (the going); `first`: run 1's end on a two-run stair (risers between the runs); `width`: run 1's outer edge. */
  kind: 'end' | 'first' | 'width';
  at: XY;
  /** The direction the grip moves along (unit). */
  axis: XY;
}

/** The grips of a stair laid out by run (none for one that cannot be laid out again). */
export function stairGrips(s: Stair): StairGrip[] {
  const lay = layoutOf(s);
  if (!lay) return [];
  const out: StairGrip[] = [];
  const last = s.runs[s.runs.length - 1];
  const fl = runFrame(last, s.location);
  out.push({ kind: 'end', at: [last.end[0], last.end[1]], axis: fl.d });
  if (s.runs.length === 2) out.push({ kind: 'first', at: [s.runs[0].end[0], s.runs[0].end[1]], axis: lay.dir });
  const f = runFrame(s.runs[0], s.location);
  // The edge away from the walking line (the right one when it runs in the middle).
  const edge = s.location === 'right' ? f.lo : f.lo + f.width;
  const side = s.location === 'right' ? -1 : 1;
  out.push({ kind: 'width', at: add(add(f.p0, mul(f.d, f.len / 2)), mul(f.right, edge)), axis: mul(f.right, side) });
  return out;
}

/** What dragging `grip` to `p` does: the update, and the readout; the reason when it cannot. */
export function stairGripEdit(s: Stair, grip: StairGrip, p: XY): { ops: ModelEditOp[]; readout: string } | { error: string } {
  const lay = layoutOf(s);
  if (!lay) return { error: 'Trappen kan ikke trækkes' };
  const h = (s.z_top - s.z_base) / Math.max(1, lay.total);
  switch (grip.kind) {
    case 'end': {
      const last = s.runs[s.runs.length - 1];
      if (last.risers < 2) return { error: 'Løbet har kun ét trin' };
      const len = dot(sub(p, [last.start[0], last.start[1]]), grip.axis);
      const going = len / (last.risers - 1);
      if (!(going >= 0.12)) return { error: 'Grunden skal være mindst 12 cm' };
      return { ops: relayout(s, { ...lay, going: r6(going) }), readout: `Grund ${cmText(going)} · 2s + g ${cmText(comfort(h, going))}` };
    }
    case 'first': {
      const proj = dot(sub(p, lay.start), lay.dir);
      const first = Math.min(Math.max(1, Math.round(proj / lay.going) + 1), lay.total - 1);
      return { ops: relayout(s, { ...lay, first }), readout: `${first} + ${lay.total - first} trin` };
    }
    case 'width': {
      const f = runFrame(s.runs[0], s.location);
      const across = dot(sub(p, f.p0), f.right);
      const w = s.location === 'centre' ? 2 * Math.abs(across) : s.location === 'left' ? across : -across;
      if (!(w >= 0.3)) return { error: 'Bredden skal være mindst 0,30 m' };
      return { ops: relayout(s, { ...lay, width: r6(w) }), readout: `Bredde ${mText(w)}` };
    }
  }
}

/** After a write: the toast's reminder that the hole in the floor above comes with Genberegn. */
export function rederiveHint(model: Pick<GeometriModel, 'storeys'>, s: Pick<Stair, 'cut_slab_above' | 'storey_to_id'>): string | null {
  if (!s.cut_slab_above || s.storey_to_id === null) return null;
  const name = model.storeys?.find((x) => x.id === s.storey_to_id)?.name ?? 'etagen over';
  return `Hullet i dækket i ${name} kommer med Genberegn`;
}

// ------------------------------------------------- optimistic overlay --

/** The stairs as they will be once `ops` are applied (creates, updates, deletes; a revert waits for the server). */
export function applyStairOps(stairs: readonly Stair[], ops: readonly ModelEditOp[]): Stair[] {
  let out = [...stairs];
  for (const op of ops) {
    if (!op.op.startsWith('stair.')) continue;
    if (op.op === 'stair.create' && 'record' in op && typeof op.record.guid === 'string') {
      const guid = op.record.guid;
      const id = Math.max(0, ...out.map((s) => s.id)) + 1;
      const row = deriveStair({
        id,
        storey_guid: null,
        storey_to_id: null,
        kind: 'straight',
        location: 'centre',
        landings: [],
        tread_depth: null,
        nosing: 0,
        cut_slab_above: false,
        built: false,
        source: 'manual',
        locked: true,
        edited: true,
        confidence: null,
        metadata: null,
        riser_count: 0,
        riser_height: null,
        goings: [],
        advice: [],
        ...(op.record as Partial<Stair>),
        guid,
      } as Stair);
      out = [...out.filter((s) => s.guid !== guid), row];
    } else if (op.op === 'stair.update' && 'set' in op) {
      out = out.map((s) => (s.guid === op.guid ? deriveStair({ ...s, ...(op.set as Partial<Stair>), edited: true, locked: true, built: false }) : s));
    } else if (op.op === 'stair.delete' && 'guid' in op) {
      out = out.filter((s) => s.guid !== op.guid);
    }
  }
  return out;
}
